/**
 * The project-scoped nav (`Led AI - Project Scoped Nav.dc.html`), as pure
 * data: which system a project's records live in, which record types that
 * system has, and — with All projects in scope — the per-project numbers the
 * picker table draws.
 *
 * ## Why records are scoped by source
 *
 * A Procore project's records are observations, tasks and punch items; an
 * Autodesk project's are Issues. Under All projects the console used to list
 * them together, so an Autodesk issue sat in "Observations" looking like an
 * observation and could not be moved on the board (the user, 2026-10-26).
 * Now All projects groups the record types by source, and picking one opens
 * a list of that source's projects; the records themselves always open
 * scoped to one project, in that project's own vocabulary.
 */

import type { ConsoleRow, ConsoleSite } from "../api";
import type { SiteActivity } from "../../server/queries/siteActivity";
import { matchesSavedView, SAVED_VIEWS, type SavedView, type SavedViewId } from "../views/ItemList";

export type RecordSource = "procore" | "autodesk" | "led";

/** A record type in the nav: a saved item view, or the Walks / Photos page. */
export type ScopedType = Exclude<SavedViewId, "all"> | "walks" | "photos";

export function sourceOf(site: Pick<ConsoleSite, "syncSource"> | undefined): RecordSource {
  return site?.syncSource === "procore" ? "procore" : site?.syncSource === "autodesk" ? "autodesk" : "led";
}

export function isRecordSource(value: string | null): value is RecordSource {
  return value === "procore" || value === "autodesk" || value === "led";
}

export const SOURCE_ORDER: readonly RecordSource[] = ["procore", "autodesk", "led"];

/** The section eyebrow under All projects. */
export const SOURCE_LABEL: Record<RecordSource, string> = { procore: "Procore", autodesk: "Autodesk", led: "Led AI only" };

/** The section eyebrow with one project in scope. */
export function scopedSectionLabel(source: RecordSource): string {
  return source === "led" ? "Records" : `${SOURCE_LABEL[source]} records`;
}

/** Each source's record types, in the design's order. */
export const SOURCE_TYPES: Record<RecordSource, readonly ScopedType[]> = {
  procore: ["walks", "log", "obs", "task", "punch", "photos"],
  led: ["walks", "log", "obs", "task", "punch", "photos"],
  autodesk: ["walks", "log", "issues", "photos"],
};

export function typeLabel(source: RecordSource, type: ScopedType): string {
  if (type === "walks") return "Walks";
  if (type === "photos") return "Photos";
  if (type === "log") return source === "autodesk" ? "Daily reports" : "Daily logs";
  if (type === "issues") return "Issues";
  return SAVED_VIEWS.find((view) => view.id === type)?.name ?? type;
}

/** The source a type belongs to when the URL names none: Issues are
 *  Autodesk's; anything else goes to the first source that has it. */
export function defaultSourceFor(type: ScopedType, present: readonly RecordSource[]): RecordSource | undefined {
  return present.find((source) => SOURCE_TYPES[source].includes(type));
}

const ISSUES: SavedView = { id: "issues", name: "Issues", hint: "", d1: "", d2: "" };

/** The rows of `type` on `siteIds` — with the list's own predicate, so a
 *  count never disagrees with the list it opens. */
export function rowsOfType(rows: readonly ConsoleRow[], type: ScopedType, siteIds: ReadonlySet<string>): ConsoleRow[] {
  if (type === "walks" || type === "photos") return [];
  const view = type === "issues" ? ISSUES : SAVED_VIEWS.find((entry) => entry.id === type)!;
  return rows.filter((row) => siteIds.has(row.jobSiteId) && matchesSavedView(view, row));
}

// ---------------------------------------------------------------------------
// The picker table's numbers
// ---------------------------------------------------------------------------

/** The two number columns, by type (the design's `COLS`). */
export function columnsFor(type: ScopedType): { a: string; b: string; bIsOverdue: boolean } {
  if (type === "walks") return { a: "Walks", b: "Photos", bIsOverdue: false };
  if (type === "photos") return { a: "Photos", b: "Walks", bIsOverdue: false };
  if (type === "log") return { a: "Logs", b: "Last 7 days", bIsOverdue: false };
  return { a: "Open", b: "Overdue", bIsOverdue: true };
}

export interface ProjectStat {
  site: ConsoleSite;
  a: number;
  b: number;
  /** Per day for the last 7 days, oldest first, today last. */
  week: number[];
  last?: Date;
  walks?: number;
  photos?: number;
}

const DAY_MS = 86_400_000;
const CLOSED = new Set(["closed", "logged"]);

function dayIndex(at: Date, today: Date): number {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const day = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
  return 6 - Math.round((start - day) / DAY_MS);
}

function lastWeek(dates: Date[], today: Date, weight: (index: number) => number = () => 1): number[] {
  const week = [0, 0, 0, 0, 0, 0, 0];
  dates.forEach((date, index) => {
    const at = dayIndex(date, today);
    if (at >= 0 && at <= 6) week[at]! += weight(index);
  });
  return week;
}

function dueDay(value: string | undefined): Date | undefined {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
}

export function projectStat(
  site: ConsoleSite,
  type: ScopedType,
  rows: readonly ConsoleRow[],
  activity: SiteActivity | undefined,
  today: Date = new Date(),
): ProjectStat {
  const walks = activity?.walks;
  const photos = activity?.photos;
  if (type === "walks" || type === "photos") {
    const recent = (activity?.recent ?? []).map((walk) => ({ at: new Date(walk.at), photos: walk.photos }));
    const isWalks = type === "walks";
    return {
      site,
      a: (isWalks ? walks : photos) ?? 0,
      b: (isWalks ? photos : walks) ?? 0,
      week: lastWeek(recent.map((walk) => walk.at), today, (index) => (isWalks ? 1 : recent[index]!.photos)),
      last: activity?.lastWalkAt ? new Date(activity.lastWalkAt) : undefined,
      walks,
      photos,
    };
  }
  const mine = rowsOfType(rows, type, new Set([site.id]));
  const created = mine.map((row) => new Date(row.createdAt));
  const moved = mine.map((row) => Date.parse(row.lastMovementAt ?? row.createdAt)).filter((at) => !Number.isNaN(at));
  const week = lastWeek(created, today);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const open = mine.filter((row) => !CLOSED.has(row.status));
  return {
    site,
    a: type === "log" ? mine.length : open.length,
    b: type === "log"
      ? week.reduce((sum, day) => sum + day, 0)
      : open.filter((row) => { const due = dueDay(row.dueDate); return !!due && due < startOfToday; }).length,
    week,
    last: moved.length ? new Date(Math.max(...moved)) : undefined,
    walks,
    photos,
  };
}

export type ProjectSort = "most" | "recent" | "az";

export function sortProjects(stats: ProjectStat[], sort: ProjectSort): ProjectStat[] {
  const copy = [...stats];
  if (sort === "az") return copy.sort((x, y) => x.site.name.localeCompare(y.site.name));
  if (sort === "recent") return copy.sort((x, y) => (y.last?.getTime() ?? 0) - (x.last?.getTime() ?? 0));
  return copy.sort((x, y) => y.a - x.a || x.site.name.localeCompare(y.site.name));
}

/** `Today`, `Yesterday`, `5 days ago`, or the date past two weeks. */
export function agoLabel(at: Date | undefined, today: Date = new Date()): string {
  if (!at) return "—";
  const days = 6 - dayIndex(at, today);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 14) return `${days} days ago`;
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric", year: at.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}
